import React, { useEffect, useRef, useState } from "react";

import { getAuthorizationHeader } from "@ai360/core";
import { selectors as cdSelectors } from "~/customer-data";
import { getLoginGuid, getTheUserCompanyGuid } from "~/login/selectors";
import { useAppSelector } from "~/store/hooks";

const chatUrl = (() => {
    const { hostname, pathname } = window.location;
    if (hostname === "localhost") return "https://analyze.ai360dev.agvance.net/chat";
    if (hostname.endsWith(".agvancesky.com")) {
        const env = pathname.match(/\/mapping([^/]*)/)?.[1] ?? "";
        return `https://analyze.ai360${env}.agvance.net/chat`;
    }
    return `https://analyze.${hostname}/chat`;
})();

import MarkdownRenderer from "../components/markdownrenderer";
import "./chat.css";

interface ChatMessage {
    content: string;
    role: "assistant" | "user";
}

interface HistoryMessage {
    content: string;
    role: string;
}

const WELCOME_MESSAGE: ChatMessage = {
    content: "Hello! I'm your AI assistant. Ask me anything about your fields and agronomic data.",
    role: "assistant",
};

function extractRealContent(buffer: string): string | null {
    const newlineIdx = buffer.indexOf("\n");
    const firstLine = newlineIdx === -1 ? buffer : buffer.substring(0, newlineIdx);
    const colonCount = (firstLine.match(/:/g) ?? []).length;
    const hasThinkingPattern = colonCount >= 3;

    if (hasThinkingPattern) {
        const hashIdx = buffer.indexOf("#");
        if (hashIdx !== -1) {
            return buffer.substring(hashIdx);
        }
        const doubleNewlineIdx = buffer.indexOf("\n\n");
        if (doubleNewlineIdx !== -1) {
            const after = buffer.substring(doubleNewlineIdx + 2);
            return after.length > 0 ? after : null;
        }
        return null;
    }

    if (buffer.includes("\n")) {
        return buffer;
    }
    return null;
}

export function AiChat(): JSX.Element {
    const selectedFieldGuidSet = useAppSelector(cdSelectors.getSelectedFieldGuids);
    const selectedFieldsMap = useAppSelector(cdSelectors.getSelectedFieldsMap);
    const loginGuid = useAppSelector(getLoginGuid);
    const companyGuid = useAppSelector(getTheUserCompanyGuid);

    const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
    const [inputText, setInputText] = useState<string>("");
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const lastSentTextRef = useRef<string>("");
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const buildHistory = (msgs: ChatMessage[]): HistoryMessage[] => {
        return msgs.map((m) => ({ content: m.content, role: m.role }));
    };

    const updateLastAssistant = (msgs: ChatMessage[], content: string): ChatMessage[] => {
        const next = [...msgs];
        next[next.length - 1] = { content, role: "assistant" };
        return next;
    };

    const sendMessage = async (text: string): Promise<void> => {
        if (!text.trim() || isStreaming) {
            return;
        }

        setErrorMessage(null);
        lastSentTextRef.current = text;

        const history = buildHistory(messages);

        setMessages((prev) => [
            ...prev,
            { content: text, role: "user" },
            { content: "", role: "assistant" },
        ]);
        setInputText("");
        setIsStreaming(true);

        let streamBuffer = "";
        let contentStarted = false;

        try {
            const authHeaders = await getAuthorizationHeader();
            const controller = new AbortController();
            abortRef.current = controller;

            const fields = selectedFieldsMap
                .valueSeq()
                .toArray()
                .map((f) => ({
                    fieldGuid: f.fieldGuid,
                    fieldName: f.name,
                    customerName: f.customerName,
                    farmName: f.farmName,
                }));
            const body = JSON.stringify({ fields, history, message: text, loginGuid, companyGuid });
            const encoded = new TextEncoder().encode(body);
            const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
            const payloadHashHex = Array.from(new Uint8Array(hashBuffer))
                .map((b) => b.toString(16).padStart(2, "0"))
                .join("");

            const response = await fetch(chatUrl, {
                body,
                headers: {
                    "Content-Type": "application/json",
                    Accept: "text/event-stream",
                    "x-amz-content-sha256": payloadHashHex,
                    "X-Auth-Token": authHeaders.Authorization,
                },
                method: "POST",
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error("No response body available");
            }

            const decoder = new TextDecoder();

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split(/\n|\}\{/).filter((l) => l.trim().length > 0);

                for (const line of lines) {
                    let jsonStr = line.trim();
                    if (!jsonStr.startsWith("{")) {
                        jsonStr = "{" + jsonStr;
                    }
                    if (!jsonStr.endsWith("}")) {
                        jsonStr = jsonStr + "}";
                    }

                    try {
                        const parsed = JSON.parse(jsonStr);
                        if (
                            parsed.type === "content_block_delta" &&
                            parsed.delta?.type === "text_delta"
                        ) {
                            streamBuffer += parsed.delta.text;

                            if (!contentStarted) {
                                const real = extractRealContent(streamBuffer);
                                if (real !== null) {
                                    contentStarted = true;
                                    streamBuffer = real;
                                    setMessages((prev) => updateLastAssistant(prev, real));
                                }
                            } else {
                                setMessages((prev) => updateLastAssistant(prev, streamBuffer));
                            }
                        }
                    } catch {
                        // Partial JSON chunk — skip and wait for next read
                    }
                }
            }

            // Flush any remaining buffer if preamble filtering held content back
            if (!contentStarted && streamBuffer.trim().length > 0) {
                setMessages((prev) => updateLastAssistant(prev, streamBuffer.trim()));
            }
        } catch (err: unknown) {
            if (err instanceof Error && err.name === "AbortError") {
                return;
            }
            setMessages((prev) => {
                const next = [...prev];
                if (next[next.length - 1]?.content === "") {
                    next.pop();
                }
                return next;
            });
            setErrorMessage("Something went wrong. Please try again.");
        } finally {
            setIsStreaming(false);
            abortRef.current = null;
        }
    };

    const retry = (): void => {
        const text = lastSentTextRef.current;
        if (!text) {
            return;
        }
        setErrorMessage(null);
        sendMessage(text);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage(inputText);
        }
    };

    const noFieldsSelected = selectedFieldGuidSet.size === 0;

    return (
        <div className="ai-chat-panel add-edit-panel">
            {noFieldsSelected && (
                <div className="ai-chat-no-fields">
                    Select one or more fields from the field list to get started.
                </div>
            )}
            <div className="ai-chat-messages">
                {messages.map((msg, idx) => (
                    <div className={`ai-chat-bubble-row ${msg.role}`} key={idx}>
                        <div className={`ai-chat-bubble ${msg.role}`}>
                            {msg.role === "assistant" ? (
                                msg.content.length > 0 ? (
                                    <MarkdownRenderer markdown={msg.content} />
                                ) : isStreaming && idx === messages.length - 1 ? (
                                    <div className="ai-chat-typing">
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                ) : null
                            ) : (
                                <span>{msg.content}</span>
                            )}
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            {errorMessage !== null && (
                <div className="ai-chat-error">
                    <span className="ai-chat-error-text">{errorMessage}</span>
                    <button className="ai-chat-retry-btn" onClick={retry} type="button">
                        ↩ Retry
                    </button>
                </div>
            )}

            <div className="ai-chat-input-bar">
                <textarea
                    className="ai-chat-input"
                    disabled={isStreaming || noFieldsSelected}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                        noFieldsSelected ? "Select fields to begin..." : "Ask about your fields..."
                    }
                    rows={1}
                    value={inputText}
                />
                <button
                    className="ai-chat-send-btn"
                    disabled={isStreaming || inputText.trim().length === 0 || noFieldsSelected}
                    onClick={() => sendMessage(inputText)}
                    type="button"
                >
                    ↑
                </button>
            </div>

            <p className="ai-chat-disclaimer">* AI Chat uses AI and can make mistakes.</p>
        </div>
    );
}
